import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import './GardenWeather.css';

interface WeatherData {
    minTemp: number | null;
    maxTemp: number | null;
    lastUpdated?: string;
}

interface GardenWeatherProps {
    googleSheetUrl?: string; // Optional for now, can be hardcoded or passed from App
}

export const GardenWeather: React.FC<GardenWeatherProps> = ({ googleSheetUrl }) => {
    const [puneWeather, setPuneWeather] = useState<WeatherData>({ minTemp: null, maxTemp: null });
    const [gardenWeather, setGardenWeather] = useState<WeatherData>({ minTemp: null, maxTemp: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch Pune Weather from Open-Meteo (Yesterday)
    useEffect(() => {
        const fetchPuneWeather = async () => {
            try {
                // Calculate yesterday's date in YYYY-MM-DD format
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const dateStr = yesterday.toISOString().split('T')[0];

                const response = await fetch(
                    `https://api.open-meteo.com/v1/forecast?latitude=18.5204&longitude=73.8567&daily=temperature_2m_max,temperature_2m_min&timezone=Asia%2FKolkata&start_date=${dateStr}&end_date=${dateStr}`
                );
                const data = await response.json();

                if (data.daily && data.daily.temperature_2m_min) {
                    setPuneWeather({
                        minTemp: data.daily.temperature_2m_min[0],
                        maxTemp: data.daily.temperature_2m_max[0],
                        lastUpdated: dateStr, // Show which date this is for
                    });
                }
            } catch (err) {
                console.error('Error fetching Pune weather:', err);
                // Don't set main error here to allow partial data display
            }
        };

        fetchPuneWeather();
    }, []);

    // Fetch Garden Weather from Google Sheet CSV
    useEffect(() => {
        const fetchGardenWeather = async () => {
            if (!googleSheetUrl) {
                // Mock data if no URL provided yet
                console.log('No Google Sheet URL provided, using mock data');
                setGardenWeather({ minTemp: 22, maxTemp: 31, lastUpdated: new Date().toLocaleDateString() });
                return;
            }

            try {
                const response = await fetch(googleSheetUrl);
                const csvText = await response.text();

                Papa.parse(csvText, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        const rows = results.data as any[];
                        if (rows.length > 0) {
                            // Use the first row (index 0) as the user will overwrite A2/B2
                            const latest = rows[0];

                            // Helper to find value with flexible key matching
                            const findValue = (obj: any, searchKeys: string[]) => {
                                const objKeys = Object.keys(obj);
                                for (const searchKey of searchKeys) {
                                    // 1. Exact match
                                    if (obj[searchKey] !== undefined) return obj[searchKey];

                                    // 2. Case-insensitive match
                                    const foundKey = objKeys.find(k =>
                                        k.toLowerCase() === searchKey.toLowerCase() ||
                                        k.toLowerCase().replace(/\s/g, '') === searchKey.toLowerCase().replace(/\s/g, '')
                                    );
                                    if (foundKey) return obj[foundKey];
                                }
                                return null;
                            };

                            const minVal = findValue(latest, ['Min Temp', 'MinTemp', 'Min', 'min_temp']);
                            const maxVal = findValue(latest, ['Max Temp', 'MaxTemp', 'Max', 'max_temp']);
                            const dateVal = findValue(latest, ['Date', 'date', 'Timestamp', 'Last Updated']);

                            setGardenWeather({
                                minTemp: minVal ? parseFloat(minVal) : null,
                                maxTemp: maxVal ? parseFloat(maxVal) : null,
                                lastUpdated: dateVal,
                            });
                        }
                    },
                    error: (err: Error) => {
                        console.error('Error parsing Google Sheet CSV:', err);
                    }
                });
            } catch (err) {
                console.error('Error fetching Garden weather:', err);
            }
        };

        fetchGardenWeather();

        // Poll every 5 minutes
        const intervalId = setInterval(fetchGardenWeather, 5 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [googleSheetUrl]);

    useEffect(() => {
        // Simple loading logic - wait a bit or until data comes in
        // Since these are async and independent, we just turn off loading after a short timeout 
        // or when both have data (if we wanted strictness). 
        // For now, just a small timeout to prevent flicker if data is fast, 
        // but practically we render what we have.
        const timer = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="garden-weather-container">
            <div className="weather-card pune-weather">
                <div className="weather-icon">🏙️</div>
                <div className="weather-info">
                    <h3>Pune City (Yesterday)</h3>
                    <div className="temp-grid">
                        <div className="temp-item">
                            <span className="label">Min</span>
                            <span className="value">{puneWeather.minTemp !== null ? `${puneWeather.minTemp}°C` : '--'}</span>
                        </div>
                        <div className="temp-item">
                            <span className="label">Max</span>
                            <span className="value">{puneWeather.maxTemp !== null ? `${puneWeather.maxTemp}°C` : '--'}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="weather-card garden-weather">
                <div className="weather-icon">🌳</div>
                <div className="weather-info">
                    <h3>Empress Garden</h3>
                    <div className="temp-grid">
                        <div className="temp-item">
                            <span className="label">Min</span>
                            <span className="value">{gardenWeather.minTemp !== null ? `${gardenWeather.minTemp}°C` : '--'}</span>
                        </div>
                        <div className="temp-item">
                            <span className="label">Max</span>
                            <span className="value">{gardenWeather.maxTemp !== null ? `${gardenWeather.maxTemp}°C` : '--'}</span>
                        </div>
                    </div>
                    {gardenWeather.lastUpdated && <div className="last-updated">Updated: {gardenWeather.lastUpdated}</div>}
                </div>
            </div>
        </div>
    );
};
